import { useCallback } from 'react'
import { HelpMessage } from 'utils/constants'
import CIMSCollapse from 'components/CIMSCollapse'
import { useCIMSNotificationsPanelContext } from 'components/CIMSNotifications/components/CIMSNotificationsPanelContext'
import {
  Group,
  useFDCNotificationsContext,
} from '../../../../FDCNotificationsContext'
import ApproverTags from '../../../ApproverTags'
import ConcurrerTags from '../../../ConcurrerTags'
import GroupFallback from '../../../GroupFallback'
import RecipientsTags from 'components/CIMSTags/common/RecipientsTags'
import { TwoDimensionalCollapseProps } from './types'
import NotConfigured from '../NotConfigured'
import { alphabeticalSort } from '@cims-online/shared'
import CIMSSection from 'components/CIMSSection'

const TwoDimensionalCollapse: React.FC<TwoDimensionalCollapseProps> = ({
  group,
}) => {
  const { event } = useCIMSNotificationsPanelContext()
  const { data, recipients, concurrerEvents, approverEvents } =
    useFDCNotificationsContext()
  const {
    byConcurrenceGroup,
    byApprovalGroup,
    byConcurrenceLevelAndApprovalGroup,
    byConcurrenceLevelAndConcurrenceGroup,
  } = recipients(event)
  const help = HelpMessage.FieldDesignChanges.Notifications
  const groups = `${group}s` as const
  const groupLevelRecipients = useCallback(
    (groupId: string, levelId: string) => {
      return group === 'Approval Group'
        ? byConcurrenceLevelAndApprovalGroup.find(
            ({ approvalGroup, concurrenceLevel }) =>
              approvalGroup.id === groupId && concurrenceLevel.id === levelId
          )?.recipients
        : byConcurrenceLevelAndConcurrenceGroup.find(
            ({ concurrenceGroup, concurrenceLevel }) =>
              concurrenceGroup.id === groupId && concurrenceLevel.id === levelId
          )?.recipients
    },
    [
      byConcurrenceLevelAndApprovalGroup,
      byConcurrenceLevelAndConcurrenceGroup,
      group,
    ]
  )

  return (
    <CIMSSection
      help={
        group === 'Approval Group'
          ? help.ApprovalGroups(event)
          : help.ConcurrenceGroups(event)
      }
      title={groups}
    >
      <CIMSCollapse
        panels={(
          (group === 'Approval Group'
            ? data?.fdcApprovalGroups
            : data?.fdcConcurrenceGroups) as Group[]
        )
          ?.sort(alphabeticalSort('name'))
          .map(({ id: groupId, name: groupName, description }) => {
            const groupRecipients =
              group === 'Approval Group'
                ? byApprovalGroup.find(
                    ({ approvalGroup }) => approvalGroup.id === groupId
                  )?.recipients
                : byConcurrenceGroup.find(
                    ({ concurrenceGroup }) => concurrenceGroup.id === groupId
                  )?.recipients
            return {
              key: groupId,
              header: {
                type: 'described',
                name: groupName,
                description,
              },
              children: (
                <>
                  {group === 'Approval Group' &&
                    approverEvents.some(
                      (approverEvent) => approverEvent.id === event.id
                    ) && (
                      <ApproverTags
                        approvalGroup={{ id: groupId, name: groupName }}
                      />
                    )}
                  {group === 'Concurrence Group' &&
                    concurrerEvents.some(
                      (concurrerEvent) => concurrerEvent.id === event.id
                    ) && (
                      <ConcurrerTags
                        concurrenceGroup={{ id: groupId, name: groupName }}
                      />
                    )}
                  <CIMSSection
                    title={group}
                    help={help.Group(groupName, event)}
                  >
                    <RecipientsTags
                      noRecipientsContent={
                        <NotConfigured type={group} name={groupName} />
                      }
                      recipients={groupRecipients}
                    />
                  </CIMSSection>
                  <CIMSSection
                    help={help.GroupLevel(groupName, event)}
                    title="Concurrence Levels"
                  >
                    <CIMSCollapse
                      noPanelsContent={
                        <GroupFallback group="Concurrence Levels" />
                      }
                      panels={data?.fdcConcurrenceLevels
                        ?.sort(alphabeticalSort('name'))
                        .map(({ id: levelId, name, description }) => {
                          const recipients = groupLevelRecipients(
                            groupId,
                            levelId
                          )
                          return {
                            key: `${groupId}_${levelId}`,
                            header: {
                              type: 'described',
                              name,
                              description,
                            },
                            children: (
                              <RecipientsTags
                                noRecipientsContent={
                                  <NotConfigured
                                    type="2D"
                                    groupType={group}
                                    groupName={groupName}
                                    levelName={name}
                                  />
                                }
                                recipients={recipients}
                              />
                            ),
                          }
                        })}
                    />
                  </CIMSSection>
                </>
              ),
            }
          })}
        noPanelsContent={<GroupFallback group={groups} />}
      />
    </CIMSSection>
  )
}

export default TwoDimensionalCollapse
